import { useEffect, useMemo, type ReactNode, type JSX } from 'react';
import { LDProvider, useLDClient } from 'launchdarkly-react-client-sdk';
import { useCurrentUser } from '@hooks/auth';
import { env } from '@config/env';

interface Props {
  children: ReactNode;
}

/**
 * Identifies the current user with LaunchDarkly whenever the auth state
 * changes. Must render inside <LDProvider>.
 */
function IdentifyOnAuth({ children }: Props): JSX.Element {
  const client = useLDClient();
  const user = useCurrentUser();

  useEffect(() => {
    if (!client) return;
    if (user) {
      void client.identify({
        kind: 'user',
        key: user.id,
        email: user.email,
        name: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email,
      });
    } else {
      void client.identify({ kind: 'user', anonymous: true, key: 'anonymous' });
    }
  }, [client, user]);

  return <>{children}</>;
}

/**
 * If no client ID is configured, skip the LD provider entirely so we don't
 * hammer LaunchDarkly's API with a placeholder key (which would log noisy
 * errors in dev).
 */
export function LaunchDarklyProvider({ children }: Props): JSX.Element {
  const clientSideID = env.VITE_LAUNCHDARKLY_CLIENT_ID;

  const context = useMemo(
    () => ({ kind: 'user' as const, anonymous: true, key: 'anonymous' }),
    [],
  );

  if (!clientSideID) {
    return <>{children}</>;
  }

  return (
    <LDProvider clientSideID={clientSideID} context={context}>
      <IdentifyOnAuth>{children}</IdentifyOnAuth>
    </LDProvider>
  );
}
